import { FC, useEffect, useRef, FormEvent, useState } from 'react';

import anime from 'animejs';
import { toast } from 'react-hot-toast';

import styles from './Contact.module.css';
import { submitContactForm } from './ContactFormSubmission';
import Decorations from '../Decorations/Decorations';
import Icon from '../Icon/Icon';

interface FormState {
  name: string;
  email: string;
  message: string;
}

interface ValidationErrors {
  name?: string;
  email?: string;
  message?: string;
}

const Contact: FC = () => {
  const formRef = useRef<HTMLFormElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [formState, setFormState] = useState<FormState>({
    name: '',
    email: '',
    message: '',
  });
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Name validation
    if (!formState.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (!/^[a-zA-Z\s\-'.]{2,50}$/.test(formState.name)) {
      newErrors.name = 'Name can only contain letters, spaces, and basic punctuation';
    }

    // Email validation
    if (!formState.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(formState.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Message validation
    if (!formState.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formState.message.length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (formState.message.length > 1000) {
      newErrors.message = 'Message must not exceed 1000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormState(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof ValidationErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading('Sending message...');

    try {
      await submitContactForm(formState, loadingToast);
      setFormState({ name: '', email: '', message: '' });
    } catch {
      // Error is already handled by submitContactForm
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!formRef.current || !buttonRef.current) return;

    const timeline = anime.timeline({
      easing: 'easeOutExpo',
    });

    timeline
      .add({
        targets: `.${styles.title}`,
        opacity: [0, 1],
        translateY: [20, 0],
        duration: 600,
      })
      .add({
        targets: `.${styles.form}`,
        opacity: [0, 1],
        translateY: [20, 0],
        duration: 800,
      })
      .add({
        targets: `.${styles.formGroup}`,
        opacity: [0, 1],
        translateY: [20, 0],
        duration: 600,
        delay: anime.stagger(100),
      })
      .add(
        {
          targets: `.${styles.socialLinks} .${styles.socialLink}`,
          opacity: [0, 1],
          scale: [0.8, 1],
          duration: 600,
          delay: anime.stagger(100),
        },
        '-=400'
      );

    // Button continuous animation
    anime({
      targets: buttonRef.current,
      scale: [1, 1.02, 1],
      boxShadow: [
        '0 4px 15px rgba(135, 160, 134, 0.3)',
        '0 8px 25px rgba(135, 160, 134, 0.5)',
        '0 4px 15px rgba(135, 160, 134, 0.3)',
      ],
      duration: 2000,
      direction: 'alternate',
      loop: true,
      easing: 'easeInOutQuad',
    });
  }, []);

  return (
    <section id="contact" className={styles.contact} aria-labelledby="contact-title">
      <h2 id="contact-title" className={styles.title}>
        Get in Touch
      </h2>

      <Decorations leftIcon="mdi:email-outline" rightIcon="mdi:chat-outline" />

      <div className={styles.container}>
        <form ref={formRef} className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formState.name}
              onChange={handleInputChange}
              required
              placeholder="Your Name"
              aria-label="Your name"
              className={errors.name ? styles.errorInput : ''}
            />
            {errors.name && <span className={styles.errorText}>{errors.name}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formState.email}
              onChange={handleInputChange}
              required
              placeholder="your.email@example.com"
              aria-label="Your email address"
              className={errors.email ? styles.errorInput : ''}
            />
            {errors.email && <span className={styles.errorText}>{errors.email}</span>}
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formState.message}
              onChange={handleInputChange}
              required
              placeholder="Your message here..."
              rows={4}
              aria-label="Your message"
              className={errors.message ? styles.errorInput : ''}
            />
            {errors.message && <span className={styles.errorText}>{errors.message}</span>}
          </div>

          <button
            ref={buttonRef}
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Message'}
            <Icon
              icon={isSubmitting ? 'mdi:loading' : 'mdi:send'}
              size="md"
              aria-label={isSubmitting ? 'Loading' : 'Send message'}
            />
          </button>
        </form>
      </div>
    </section>
  );
};

export default Contact;
